import { useState, useRef, useCallback, useEffect } from "react";
import AgoraRTC, {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from "agora-rtc-sdk-ng";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

AgoraRTC.setLogLevel(3); // warnings only

export interface AgoraParticipant {
  uid: string | number;
  displayName: string;
  videoTrack?: any;
  audioTrack?: any;
}

export function useAgoraRoom() {
  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const localVideoRef = useRef<ICameraVideoTrack | null>(null);
  const localAudioRef = useRef<IMicrophoneAudioTrack | null>(null);
  const [joined, setJoined] = useState(false);
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);

  const join = useCallback(async (channelName: string) => {
    setIsConnecting(true);
    try {
      // Get Agora credentials from edge function
      const { data, error } = await supabase.functions.invoke("agora-token", {
        body: { channelName, uid: 0 },
      });
      if (error || !data?.appId) throw new Error("Failed to get Agora credentials");

      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      clientRef.current = client;

      // Subscribe to remote user events
      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        setRemoteUsers(prev => {
          const exists = prev.find(u => u.uid === user.uid);
          if (exists) return prev.map(u => u.uid === user.uid ? user : u);
          return [...prev, user];
        });
      });

      client.on("user-unpublished", (user, mediaType) => {
        setRemoteUsers(prev => prev.map(u => u.uid === user.uid ? user : u));
      });

      client.on("user-left", (user) => {
        setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
      });

      // Join channel
      await client.join(data.appId, channelName, data.token || null, data.uid || 0);

      // Create and publish local tracks
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
      localAudioRef.current = audioTrack;
      localVideoRef.current = videoTrack;
      await client.publish([audioTrack, videoTrack]);

      setJoined(true);
    } catch (e: any) {
      toast({ title: "Connection Error", description: e.message, variant: "destructive" });
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const leave = useCallback(async () => {
    localAudioRef.current?.close();
    localVideoRef.current?.close();
    await clientRef.current?.leave();
    clientRef.current = null;
    localAudioRef.current = null;
    localVideoRef.current = null;
    setRemoteUsers([]);
    setJoined(false);
    setVideoEnabled(true);
    setAudioEnabled(true);
  }, []);

  const toggleVideo = useCallback(async () => {
    if (localVideoRef.current) {
      await localVideoRef.current.setEnabled(!videoEnabled);
      setVideoEnabled(!videoEnabled);
    }
  }, [videoEnabled]);

  const toggleAudio = useCallback(async () => {
    if (localAudioRef.current) {
      await localAudioRef.current.setEnabled(!audioEnabled);
      setAudioEnabled(!audioEnabled);
    }
  }, [audioEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      localAudioRef.current?.close();
      localVideoRef.current?.close();
      clientRef.current?.leave();
    };
  }, []);

  return {
    join,
    leave,
    joined,
    isConnecting,
    remoteUsers,
    localVideoTrack: localVideoRef.current,
    localAudioTrack: localAudioRef.current,
    videoEnabled,
    audioEnabled,
    toggleVideo,
    toggleAudio,
  };
}
